import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  Select,
  SelectOption,
  TextInput,
} from "@utahdts/utah-design-system";
import {
  GET_SECTION_CONDITION,
  SAVE_SECTION_CONDITION,
} from "../../queries/siteVisitQrys/sectionConditionsQrys";
import { GET_STATUS_OPTIONS } from "../../queries/waterlinkQrys";
import SectionConditionFacilityFilter from "./SectionConditionFacilityFilter";
import SectionConditionFacilityCountTest from "./SectionConditionFacilityCountTest";
import { formatDate } from "../../utilities/commonFunctions/formattingFunctions";
import "./siteVisits.css";

const EMPTY_FORM = {
  description: "",
  showGroupDescription: false,
  status: "active",
  sql: "",
  meta: {
    createdUser: "",
    createdTs: "",
    lastUpdatedUser: "",
    lastUpdatedTs: "",
  },
};

const SectionConditionModal = ({
  onClose,
  selectedConditionId,
  canEdit,
  refetch,
}) => {
  const isNewCondition = selectedConditionId == null;

  const [form, setForm] = useState(EMPTY_FORM);
  const [descriptionError, setDescriptionError] = useState("");
  const [statusError, setStatusError] = useState("");
  const [saveError, setSaveError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [facilityTypes, setFacilityTypes] = useState([]);
  const [treatmentProcesses, setTreatmentProcesses] = useState([]);
  const [storageTankTypes, setStorageTankTypes] = useState([]);
  const [facilityStatuses, setFacilityStatuses] = useState([]);
  const [facilityStatusReasons, setFacilityStatusReasons] = useState([]);
  const [systemIndicators, setSystemIndicators] = useState([]);
  const [facilityIndicators, setFacilityIndicators] = useState([]);

  const [fetchCondition, { data: conditionData }] = useLazyQuery(GET_SECTION_CONDITION, { fetchPolicy: "network-only" });
  const [saveCondition] = useMutation(SAVE_SECTION_CONDITION);
  const { data: statusData } = useQuery(GET_STATUS_OPTIONS);
  const statusOptions = statusData?.getStatusOptions ?? [];
  const conditionLoading = !isNewCondition && !conditionData;

  useEffect(() => {
    setSaveError(null);
    setDescriptionError("");
    setStatusError("");

    if (isNewCondition) {
      setForm(EMPTY_FORM);
      setFacilityTypes([]);
      setTreatmentProcesses([]);
      setStorageTankTypes([]);
      setFacilityStatuses([]);
      setFacilityStatusReasons([]);
      setSystemIndicators([]);
      setFacilityIndicators([]);
      return;
    }

    fetchCondition({ variables: { conditionId: selectedConditionId } });
  }, [isNewCondition, selectedConditionId, fetchCondition]);

  useEffect(() => {
    if (!conditionData?.getSectionConditionById) return;
    const condition = conditionData.getSectionConditionById;
    setForm({
      description: condition.description ?? "",
      showGroupDescription: condition.showGroupDescription ?? false,
      status: condition.status ?? "active",
      sql: condition.sql ?? "",
      meta: {
        createdUser: condition.createdUser ?? "",
        createdTs: condition.createdTs ?? "",
        lastUpdatedUser: condition.lastUpdatedUser ?? "",
        lastUpdatedTs: condition.lastUpdatedTs ?? "",
      },
    });
    setFacilityTypes(condition.facilityFilter?.facilityTypes ?? []);
    setTreatmentProcesses(condition.facilityFilter?.treatmentProcesses ?? []);
    setStorageTankTypes(condition.facilityFilter?.storageTankTypes ?? []);
    setFacilityStatuses(condition.facilityFilter?.facilityStatuses ?? []);
    setFacilityStatusReasons(condition.facilityFilter?.facilityStatusReasons ?? []);
    setSystemIndicators(condition.facilityFilter?.systemIndicators ?? []);
    setFacilityIndicators(condition.facilityFilter?.facilityIndicators ?? []);
  }, [conditionData]);

  const facilityFilter = useMemo(() => ({
    facilityTypes,
    facilityStatuses,
    treatmentProcesses,
    storageTankTypes,
    facilityStatusReasons,
    systemIndicators: systemIndicators.map(({ indicatorName, indicatorValues }) => ({ indicatorName, indicatorValues })),
    facilityIndicators: facilityIndicators.map(({ indicatorName, indicatorValues }) => ({ indicatorName, indicatorValues })),
  }), [facilityTypes, facilityStatuses, treatmentProcesses, storageTankTypes, facilityStatusReasons, systemIndicators, facilityIndicators]);

  const handleSave = async () => {
    setSaveError(null);
    setDescriptionError("");
    setStatusError("");

    if (!form.description.trim()) {
      setDescriptionError("Description is required.");
      return;
    }
    if (!form.status.trim()) {
      setStatusError("Status is required.");
      return;
    }

    setIsSaving(true);
    try {
      await saveCondition({
        variables: {
          input: {
            conditionId: selectedConditionId,
            description: form.description,
            showGroupDescription: form.showGroupDescription,
            status: form.status,
            facilityFilter,
          },
        },
      });
      await refetch();
      onClose();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal className="modal--large" onClose={onClose} onEscape={onClose}>
      <ModalTitle>{canEdit ? "Section Condition" : "Section Condition (View Only)"}</ModalTitle>
      <ModalContent className="site-visits-condition-modal-content">
        {conditionLoading ? (
          <p>Loading...</p>
        ) : (
          <div className="site-visits-option-fields-grid">
            <div>
              <TextInput
                id="condition-description"
                label="Description"
                isRequired
                isDisabled={!canEdit}
                errorMessage={descriptionError}
                value={form.description}
                onChange={(e) => {
                  setDescriptionError("");
                  setForm((prev) => ({ ...prev, description: e.target.value }));
                }}
                maxLength={255}
                className="site-visits-field-control"
              />
            </div>
            <div>
              <Select
                id="condition-show-group"
                label="Show Question Group Description"
                isDisabled={!canEdit}
                value={form.showGroupDescription ? "true" : "false"}
                onChange={(e) => setForm((prev) => ({ ...prev, showGroupDescription: e.target.value === "true" }))}
                className="site-visits-field-control"
              >
                <SelectOption label="No" value="false" />
                <SelectOption label="Yes" value="true" />
              </Select>
            </div>
            <div>
              <Select
                id="condition-status"
                label="Status"
                isRequired
                isDisabled={!canEdit}
                errorMessage={statusError}
                value={form.status}
                onChange={(e) => {
                  setStatusError("");
                  setForm((prev) => ({ ...prev, status: e.target.value }));
                }}
                className="site-visits-field-control"
              >
                {statusOptions.map((s) => (
                  <SelectOption key={s} label={s.charAt(0).toUpperCase() + s.slice(1)} value={s} />
                ))}
              </Select>
            </div>
            <SectionConditionFacilityFilter
              facilityTypes={facilityTypes}
              treatmentProcesses={treatmentProcesses}
              storageTankTypes={storageTankTypes}
              facilityStatuses={facilityStatuses}
              facilityStatusReasons={facilityStatusReasons}
              systemIndicators={systemIndicators}
              facilityIndicators={facilityIndicators}
              onFacilityTypesChange={setFacilityTypes}
              onTreatmentProcessesChange={setTreatmentProcesses}
              onStorageTankTypesChange={setStorageTankTypes}
              onFacilityStatusesChange={setFacilityStatuses}
              onFacilityStatusReasonsChange={setFacilityStatusReasons}
              onSystemIndicatorsChange={setSystemIndicators}
              onFacilityIndicatorsChange={setFacilityIndicators}
              canEdit={canEdit}
            />
            <div className="site-visits-condition-sql">
              <label htmlFor="condition-sql"><strong>Condition SQL:</strong></label>
              <textarea
                id="condition-sql"
                value={form.sql}
                readOnly
                rows={10}
                className="site-visits-field-control"
              />
            </div>
            {!isNewCondition && (
              <>
                <div>
                  <strong>Last Updated:</strong>
                  <span className="site-visits-meta-value">
                    {form.meta.lastUpdatedTs
                      ? `${formatDate(form.meta.lastUpdatedTs)} by ${form.meta.lastUpdatedUser}`
                      : "—"}
                  </span>
                </div>
                <div>
                  <strong>Created:</strong>
                  <span className="site-visits-meta-value">
                    {form.meta.createdTs
                      ? `${formatDate(form.meta.createdTs)} by ${form.meta.createdUser}`
                      : "—"}
                  </span>
                </div>
              </>
            )}
            <SectionConditionFacilityCountTest facilityFilter={facilityFilter} />
          </div>
        )}
        {saveError && (
          <p className="site-visits-save-error">{saveError}</p>
        )}
      </ModalContent>
      <ModalFooter>
        <div className="site-visits-modal-footer-actions">
          <Button variant="secondary" onClick={onClose}>
            {canEdit ? "Cancel" : "Close"}
          </Button>
          {canEdit && (
            <Button
              appearance="solid"
              variant="primary"
              onClick={handleSave}
              isDisabled={isSaving}
            >
              Save
            </Button>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default SectionConditionModal;
